import type {
  CurriculumCoreItem,
  CurriculumCoreSchool,
  CurriculumOpenStatus,
  TrieKey,
} from '@mathflat/domain/Curriculum'
import {
  type GradeElementaryMiddle,
  SCHOOL_TYPE_KO_ABBR,
  SCHOOL_TYPE_KO_FULL,
  type SchoolType,
  type Semester,
} from '@mathflat/domain/SchoolSystem'

/** 선생님 요금제가 그 학교급·학년·학기를 구독했는지. 솔로 요금제만 UNSUBSCRIBED 가 생긴다 */
export type SubscribeStatus = 'SUBSCRIBED' | 'PART_SUBSCRIBED' | 'UNSUBSCRIBED'
type Subscribable = { subscribeStatus: SubscribeStatus }

export type SubscribableCurriculumCoreItem<T extends SchoolType = SchoolType> =
  CurriculumCoreItem<T> & Subscribable

export type SubscribableCurriculumCoreSchool<T extends SchoolType = SchoolType> =
  CurriculumCoreSchool<T> & Subscribable

export type AllTypeCurriculumCoreSchool = Omit<CurriculumCoreSchool, 'value'> &
  Subscribable & { value: 'ALL' }

export const makeAllTypeCurriculumCoreSchool = (): AllTypeCurriculumCoreSchool => ({
  value: 'ALL',
  label: '전체',
  labelFull: '전체',
  revisions: ['CURRICULUM_15', 'CURRICULUM_22'],
  trieKeys: ['ALL'],
  status: 'ACTIVE',
  subscribeStatus: 'SUBSCRIBED',
})

/** 학년·학기·과목 드롭다운의 "전체" 선택지 */
export type AllTypeCurriculumCoreItem = {
  isAllType: true
  trieKey: 'ALL'
  upperTrieKey: 'ALL'
  lowerTrieKeys: ['ALL']
  revision: undefined
  schoolType: SchoolType | null
  gradeSubject: undefined
  semester: null
  categorizedGrade: undefined
  status: CurriculumOpenStatus
  subscribeStatus: SubscribeStatus
  label: string
  labelShort: string
  labelWithoutSchoolType: string
  labelWithSpace: string
}

export const makeAllTypeCurriculumCoreItem = ({
  schoolType,
  label,
}: {
  schoolType: SchoolType | null
  label?: string
}): AllTypeCurriculumCoreItem => {
  const _label = label || (schoolType === 'HIGH' ? '과목 전체' : '학년·학기')
  return {
    isAllType: true,
    trieKey: 'ALL',
    upperTrieKey: 'ALL',
    lowerTrieKeys: ['ALL'],
    revision: undefined,
    schoolType: schoolType || null,
    gradeSubject: undefined,
    semester: null,
    categorizedGrade: undefined,
    status: 'ACTIVE',
    subscribeStatus: 'SUBSCRIBED',
    label: _label,
    labelShort: _label,
    labelWithoutSchoolType: _label,
    labelWithSpace: _label,
  }
}

/** 개정만 다른 같은 학년·학기(고등은 같은 학년으로 분류되는 과목들)를 하나로 묶은 항목. 초1, 초1-1, 중1-1, 고1 */
export type UnionGradeCurriculumCoreItem<T extends SchoolType = SchoolType> = {
  isAllType: false
  /** 묶인 항목들의 trieKey 를 UNION_CURRICULUM_KEY 로 이은 값 */
  trieKey: TrieKey
  upperTrieKey: TrieKey
  lowerTrieKeys: TrieKey[]
  revision: undefined
  schoolType: T
  gradeSubject: GradeElementaryMiddle
  semester: Semester
  /** ex) 초등학교1, 초등학교1-1, 고등학교1 */
  label: string
  /** ex) 초1, 초1-1, 고1 */
  labelShort: string
  /** ex) 1 - 1, 1 */
  labelWithoutSchoolType: string
  /** ex) 초 1 - 1, 고 1 */
  labelWithSpace: string
  categorizedGrade: GradeElementaryMiddle
  status: CurriculumOpenStatus
  subscribeStatus: SubscribeStatus
}

export const UNION_CURRICULUM_KEY = '_UNION_'

const UNION_STATUS_PRIORITY: CurriculumOpenStatus[] = ['HIDDEN', 'PRE_OPEN', 'NOT_READY', 'ACTIVE']

export const makeUnionGradeCurriculumCoreItem = <T extends SchoolType>(
  curriculums: CurriculumCoreItem<T>[],
): UnionGradeCurriculumCoreItem<T> => {
  const [first] = curriculums
  const { schoolType } = first
  const gradeSubject =
    schoolType === 'HIGH' ? first.categorizedGrade : (first.gradeSubject as GradeElementaryMiddle)
  const semester = schoolType === 'HIGH' ? null : first.semester

  const school = SCHOOL_TYPE_KO_ABBR[schoolType]
  const schoolFull = SCHOOL_TYPE_KO_FULL[schoolType]
  const semesterSuffix = semester ? `-${semester}` : ''
  const labelWithoutSchoolType = `${gradeSubject}${semester ? ` - ${semester}` : ''}`

  return {
    isAllType: false,
    trieKey: curriculums.map(({ trieKey }) => trieKey).join(UNION_CURRICULUM_KEY),
    upperTrieKey: curriculums.map(({ upperTrieKey }) => upperTrieKey).join(UNION_CURRICULUM_KEY),
    lowerTrieKeys: [...new Set(curriculums.flatMap(({ lowerTrieKeys }) => lowerTrieKeys))],
    revision: undefined,
    schoolType,
    gradeSubject,
    semester,
    label: `${schoolFull}${gradeSubject}${semesterSuffix}`,
    labelShort: `${school}${gradeSubject}${semesterSuffix}`,
    labelWithoutSchoolType,
    labelWithSpace: `${school} ${labelWithoutSchoolType}`,
    categorizedGrade: gradeSubject,
    status:
      UNION_STATUS_PRIORITY.find((status) => curriculums.some((item) => item.status === status)) ??
      'ACTIVE',
    subscribeStatus: 'SUBSCRIBED',
  }
}
